'use client';

// DÉCORS — de faux « clichés de voyage » dessinés entièrement en CSS.
//
// Pourquoi pas de vraies photos : aucune image à charger (donc rien à télécharger, rien à
// optimiser, pas de droits à gérer), net sur tous les écrans, et surtout des couleurs qui
// s'accordent au crème/encre/menthe de la marque. Chaque décor est une composition simple —
// ciel dégradé, soleil, ligne d'horizon, reliefs — qui se lit instantanément comme une photo.
//
// Ces couleurs sont DÉCORATIVES (elles évoquent des paysages) : elles ne sortent pas des tokens
// sémantiques d'interface, et ne doivent pas y être rattachées.

export type SceneryKind =
  | 'lagoon'
  | 'dawn'
  | 'forest'
  | 'night'
  | 'city'
  | 'snow'
  | 'desert';

const SKIES: Record<SceneryKind, string> = {
  lagoon:
    'linear-gradient(180deg, #F9D89B 0%, #F2A469 38%, #D9714A 58%, #2E6489 100%)',
  dawn: 'linear-gradient(180deg, #FBE3B8 0%, #F2B075 40%, #C97A5E 70%, #6B4E63 100%)',
  forest:
    'linear-gradient(180deg, #CFE3B4 0%, #7FAE72 42%, #3F7355 72%, #234A3C 100%)',
  night:
    'linear-gradient(180deg, #16233B 0%, #1E3454 45%, #2B4A6B 72%, #10192B 100%)',
  city: 'linear-gradient(180deg, #F6C9A2 0%, #D98E7E 40%, #7C5A7A 72%, #2E2B45 100%)',
  snow: 'linear-gradient(180deg, #E8F1F7 0%, #BBD3E4 42%, #8CAEC6 70%, #4E6C86 100%)',
  desert:
    'linear-gradient(180deg, #FAE0AF 0%, #E8A96A 40%, #C16E45 70%, #7A3F32 100%)',
};

// Teinte des reliefs (plus sombre que le ciel pour créer la découpe).
const RIDGES: Record<SceneryKind, string> = {
  lagoon: '#1D4763',
  dawn: '#4A3550',
  forest: '#1B3A2F',
  night: '#0B1322',
  city: '#211E36',
  snow: '#3A5470',
  desert: '#5C2F26',
};

interface SceneryProps {
  kind: SceneryKind;
  // Affiche un soleil / une lune (haut de l'image).
  sun?: boolean;
  className?: string;
}

export function Scenery({ kind, sun = true, className = '' }: SceneryProps) {
  const ridge = RIDGES[kind];
  const isNight = kind === 'night';

  return (
    <div
      aria-hidden
      className={`absolute inset-0 overflow-hidden ${className}`}
    >
      {/* Ciel */}
      <div className="absolute inset-0" style={{ background: SKIES[kind] }} />

      {/* Étoiles (nuit seulement) — positions fixes pour éviter tout aléa au rendu. */}
      {isNight && (
        <div className="absolute inset-0">
          {[
            [12, 14],
            [28, 8],
            [46, 18],
            [63, 10],
            [78, 22],
            [88, 12],
            [36, 28],
            [70, 32],
          ].map(([left, top], i) => (
            <span
              key={i}
              className="rmbr-twinkle absolute size-[2px] rounded-full bg-white"
              style={{
                left: `${left}%`,
                top: `${top}%`,
                animationDelay: `${i * 420}ms`,
              }}
            />
          ))}
        </div>
      )}

      {/* Soleil ou lune, avec son halo */}
      {sun && (
        <div
          className="absolute rounded-full"
          style={{
            left: '64%',
            top: isNight ? '12%' : '22%',
            width: isNight ? 26 : 42,
            height: isNight ? 26 : 42,
            background: isNight
              ? 'radial-gradient(circle, #EEF3FA 55%, rgba(238,243,250,0) 72%)'
              : 'radial-gradient(circle, #FFF6DF 42%, rgba(255,236,190,0.55) 62%, rgba(255,236,190,0) 78%)',
            boxShadow: isNight
              ? '0 0 26px 10px rgba(220,232,248,0.35)'
              : '0 0 44px 18px rgba(255,225,170,0.5)',
          }}
        />
      )}

      {/* Relief arrière (plus clair, plus loin) */}
      <svg
        className="absolute inset-x-0 bottom-0 h-[38%] w-full"
        viewBox="0 0 100 40"
        preserveAspectRatio="none"
      >
        <path
          d="M0 26 L18 12 L32 22 L48 6 L64 20 L80 10 L100 24 L100 40 L0 40 Z"
          fill={ridge}
          opacity="0.45"
        />
      </svg>

      {/* Relief avant (net, sombre) */}
      <svg
        className="absolute inset-x-0 bottom-0 h-[26%] w-full"
        viewBox="0 0 100 28"
        preserveAspectRatio="none"
      >
        <path
          d="M0 20 L14 10 L30 18 L44 8 L58 17 L74 9 L88 18 L100 12 L100 28 L0 28 Z"
          fill={ridge}
        />
      </svg>

      {/* Voile de lumière en bas : sert de fondu pour poser du texte par-dessus. */}
      <div
        className="absolute inset-x-0 bottom-0 h-1/2"
        style={{
          background:
            'linear-gradient(to top, rgba(10,14,22,0.72), transparent)',
        }}
      />
    </div>
  );
}
